import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  MapPin, 
  Building2, 
  Users, 
  GraduationCap, 
  Briefcase,
  CheckCircle,
  Share2,
  Link as LinkIcon,
  Mail,
  ExternalLink,
  Trash2,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { LocationCard } from "@/components/brokerages/LocationCard";
import { LeadCaptureForm } from "@/components/brokerages/LeadCaptureForm";
import { BrokerageHeroSection } from "@/components/brokerages/BrokerageHeroSection";
import { BrokerageAtAGlance } from "@/components/brokerages/BrokerageAtAGlance";
import { BrokerageSidebar } from "@/components/brokerages/BrokerageSidebar";
import { BrokerageStickyHeader } from "@/components/brokerages/BrokerageStickyHeader";
import { BrokerageTestimonialVideos } from "@/components/brokerages/BrokerageTestimonialVideos";
import { CommissionCalculator } from "@/components/brokerages/CommissionCalculator";
import { FeeBreakdownCard } from "@/components/brokerages/FeeBreakdownCard";
import { FeatureSections } from "@/components/product/FeatureSections";
import { useBrokerage, useBrokerageLocations } from "@/hooks/useBrokerages";
import { useAgentLocation } from "@/hooks/useAgentLocation";
import { useBrokerageEditor } from "@/hooks/useBrokerageEditor";
import { useAuth } from "@/contexts/AuthContext";
import { useEditMode } from "@/contexts/EditModeContext";
import { toast } from "sonner";
import { EditableText } from "@/components/admin/EditableText";
import { EditableFeatureSections } from "@/components/admin/EditableFeatureSections";
import { EditableTestimonialVideos } from "@/components/admin/EditableTestimonialVideos";
import { PublishButton } from "@/components/admin/PublishButton";
import { 
  BEST_FOR_LABELS, 
  PRIORITIES_LABELS,
} from "@/contracts/brokerage";
import type { BrokerageLocation, Brokerage } from "@/contracts/brokerage";

export default function BrokerageDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { location } = useAgentLocation();
  const { data: brokerage, isLoading } = useBrokerage(slug || '');
  const { data: locations } = useBrokerageLocations(
    brokerage?.id || '', 
    location?.lat && location?.lng ? { lat: location.lat, lng: location.lng, state: location.state } : undefined
  );

  const { isEditor } = useAuth();
  const { isEditMode } = useEditMode();
  const { updateBrokerage, deleteBrokerage, publishBrokerage, isDeleting, isPublishing } = useBrokerageEditor();

  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<BrokerageLocation | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [headerVisible, setHeaderVisible] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Track header visibility on scroll
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setHeaderVisible(currentScrollY < 100 || currentScrollY < lastScrollY);
      lastScrollY = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set initial selected image
  useEffect(() => {
    if (brokerage && !selectedImage) {
      setSelectedImage(brokerage.image || brokerage.logo || '');
    }
  }, [brokerage, selectedImage]);

  const handleUpdateField = async (field: string, value: any) => {
    if (!slug) return;
    updateBrokerage({ brokerageSlug: slug, field, value });
  };

  const handleDelete = () => {
    if (!slug) return;
    deleteBrokerage(slug, {
      onSuccess: () => {
        navigate('/brokerages');
      },
    });
  };

  const handlePublish = () => {
    if (!slug) return;
    publishBrokerage(slug);
  };

  const handleBookClick = (loc: BrokerageLocation) => {
    if (loc.booking_url) {
      window.open(loc.booking_url, '_blank');
    } else {
      setSelectedLocation(loc);
      setLeadFormOpen(true);
    }
  };

  const handleGeneralInquiry = () => {
    setSelectedLocation(null);
    setLeadFormOpen(true);
  };

  const brokerageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(brokerageUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Check out ${brokerage?.name || 'this brokerage'}`);
    const body = encodeURIComponent(`I thought you might be interested in this: ${brokerageUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6 max-w-6xl">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-80 w-full rounded-lg mb-6" />
          <div className="grid lg:grid-cols-[2fr,0.75fr] gap-8">
            <div className="space-y-6">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-64 w-full rounded-lg" />
            </div>
            <div>
              <Skeleton className="h-96 w-full rounded-lg" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!brokerage) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12 text-center">
          <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Brokerage Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The brokerage you're looking for doesn't exist.
          </p>
          <Link to="/brokerages">
            <Button>Browse Brokerages</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header forceVisible={headerVisible} />
      
      <main className="flex-1">
        {/* Sticky Price Bar */}
        <BrokerageStickyHeader 
          brokerage={brokerage} 
          headerVisible={headerVisible}
          onRequestInfo={handleGeneralInquiry}
          onUpdateField={handleUpdateField}
        />

        {/* Breadcrumb Navigation */}
        <div className="container mx-auto max-w-6xl px-4 pt-4 pb-2">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <Breadcrumb>
                <BreadcrumbList className="flex-wrap-none text-xs sm:text-sm">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/">Home</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/brokerages">Brokerages</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem className="min-w-0">
                    <BreadcrumbPage className="truncate max-w-[150px] sm:max-w-none">{brokerage.name}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            
            <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2">
              {/* Editor Controls */}
              {isEditor && isEditMode && (
                <>
                  <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" className="h-7 text-[10px] sm:text-xs gap-1 px-2 sm:px-3">
                        <Trash2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Brokerage?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete "{brokerage?.name}" and all associated data. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {isDeleting ? 'Deleting...' : 'Delete Brokerage'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-[10px] sm:text-xs gap-1 sm:gap-1.5 px-2 sm:px-3">
                    <Share2 className="h-3.5 w-3.5" />
                    <span>Share</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm mb-3">Share this brokerage</h4>
                    <Button variant="outline" className="w-full justify-start" onClick={handleCopyLink}>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Copy Link
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handleShareEmail}>
                      <Mail className="h-4 w-4 mr-2" />
                      Share via Email
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              
              {brokerage.website_url && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-[10px] sm:text-xs gap-1 px-2 sm:px-3"
                  onClick={() => window.open(brokerage.website_url!, '_blank')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Website</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Hero Tagline */}
        <div className="container mx-auto max-w-6xl px-4 pb-1 pt-1">
          <h2 className="text-xl md:text-2xl font-bold text-left text-foreground">
            <EditableText
              value={brokerage.subtitle || ''}
              onSave={(value) => handleUpdateField('subtitle', value)}
              as="span"
            />
          </h2>
        </div>

        {/* Hero Section with At-a-Glance in left column */}
        <section className="bg-background py-2 md:py-4">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid lg:grid-cols-[2fr,0.75fr] gap-8 items-start">
              {/* Left Column - Hero + At-a-Glance */}
              <div className="space-y-6">
                <BrokerageHeroSection
                  brokerage={brokerage}
                  selectedImage={selectedImage}
                  onImageSelect={setSelectedImage}
                  onUpdateField={handleUpdateField}
                />
                
                {/* At-a-Glance inside left column */}
                <BrokerageAtAGlance brokerage={brokerage} />
              </div>
              
              {/* Right Column - Sticky Sidebar */}
              <div className="lg:sticky lg:top-40">
                <BrokerageSidebar 
                  brokerage={brokerage} 
                  onRequestInfo={handleGeneralInquiry}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Feature Sections */}
        {(brokerage.show_feature_sections !== false && brokerage.feature_sections && brokerage.feature_sections.length > 0) || (isEditor && isEditMode) ? (
          <section className="py-12 bg-background">
            <div className="container mx-auto px-4 max-w-6xl">
              <h2 className="text-2xl font-bold mb-8">Why Agents Choose {brokerage.name}</h2>
              <FeatureSections sections={brokerage.feature_sections as { headline: string; bullets: string[]; imageUrl?: string }[] || []} />
              
              {/* Editable Feature Sections */}
              <EditableFeatureSections
                sections={brokerage.feature_sections as { headline: string; bullets: string[]; imageUrl?: string }[] || []}
                onSave={(sections) => handleUpdateField('feature_sections', sections)}
                isVisible={brokerage.show_feature_sections !== false}
                onVisibilityChange={(visible) => handleUpdateField('show_feature_sections', visible)}
              />
            </div>
          </section>
        ) : null}

        {/* Commission Calculator */}
        <section id="calculator" className="py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl font-bold mb-6">See How Much You'd Make</h2>
            <CommissionCalculator brokerage={brokerage} />
          </div>
        </section>

        {/* Testimonial Videos */}
        {((brokerage.show_testimonials !== false && brokerage.testimonial_videos && brokerage.testimonial_videos.length > 0) || (isEditor && isEditMode)) && (
          <section id="testimonials" className="py-8 bg-background">
            <div className="container mx-auto px-4 max-w-6xl">
              <h2 className="text-2xl font-bold mb-6">What Agents Are Saying</h2>
              <BrokerageTestimonialVideos videos={brokerage.testimonial_videos as { video_url: string; thumbnail_url?: string; author: string; company: string; quote: string }[] || []} />
              
              {/* Editable Testimonial Videos */}
              <EditableTestimonialVideos
                videos={brokerage.testimonial_videos as { video_url: string; thumbnail_url: string; author: string; company: string; quote: string }[] || []}
                onSave={(videos) => handleUpdateField('testimonial_videos', videos)}
              />
            </div>
          </section>
        )}

        {/* Fee Breakdown */}
        <section id="compensation" className="py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl font-bold mb-6">Compensation Breakdown</h2>
            <FeeBreakdownCard brokerage={brokerage} />
          </div>
        </section>

        {/* Culture & Locations Tabs */}
        <section id="culture" className="py-8 bg-background">
          <div className="container mx-auto px-4 max-w-6xl">
            <Tabs defaultValue="culture" className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md">
                <TabsTrigger value="culture">Culture & Support</TabsTrigger>
                <TabsTrigger value="locations">Locations</TabsTrigger>
              </TabsList>

              <TabsContent value="culture" className="mt-6 space-y-4">
                {/* What Makes Them Different */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">What Makes {brokerage.name} Different</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EditableText
                      value={brokerage.what_makes_us_different || ''}
                      onSave={(value) => handleUpdateField('what_makes_us_different', value)}
                      multiline
                      as="p"
                      className="text-muted-foreground whitespace-pre-wrap"
                    />
                  </CardContent>
                </Card>

                {/* Best For */}
                {brokerage.best_for && brokerage.best_for.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Best For</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {brokerage.best_for.map((item) => (
                          <Badge key={item} variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 py-1.5 px-3">
                            <CheckCircle className="h-4 w-4 mr-1.5" />
                            {BEST_FOR_LABELS[item] || item}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Support & Culture */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Support & Culture</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">Training</p>
                        <EditableText
                          value={brokerage.training_availability || ''}
                          onSave={(value) => handleUpdateField('training_availability', value)}
                          as="p"
                          className="text-sm text-muted-foreground"
                        />
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">Mentorship</p>
                        <EditableText
                          value={brokerage.mentorship_structure || ''}
                          onSave={(value) => handleUpdateField('mentorship_structure', value)}
                          as="p"
                          className="text-sm text-muted-foreground"
                        />
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">Compensation Model</p>
                        <EditableText
                          value={brokerage.compensation_model || ''}
                          onSave={(value) => handleUpdateField('compensation_model', value)}
                          as="p"
                          className="text-sm text-muted-foreground"
                        />
                      </div>
                    </div>
                    {brokerage.priorities && brokerage.priorities.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <p className="font-medium mb-2">They Prioritize</p>
                          <div className="flex flex-wrap gap-2">
                            {brokerage.priorities.map((priority) => (
                              <Badge key={priority} variant="outline">
                                {PRIORITIES_LABELS[priority] || priority}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Operating States */}
                {brokerage.operating_states && brokerage.operating_states.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Operating States</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {brokerage.operating_states.map((state) => (
                          <Badge key={state} variant="secondary">{state}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="locations" id="locations" className="mt-6">
                {locations && locations.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Offices {location?.state ? `in ${location.state}` : 'Near You'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {locations.map((loc) => (
                        <LocationCard
                          key={loc.id}
                          location={loc}
                          distance={loc.distance_miles}
                          onBookClick={handleBookClick}
                        />
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No office locations found in your area.</p>
                      <Button variant="outline" className="mt-4" onClick={handleGeneralInquiry}>
                        Request Information Anyway
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      {brokerage && (
        <LeadCaptureForm
          open={leadFormOpen}
          onOpenChange={setLeadFormOpen}
          brokerage={brokerage as Brokerage}
          location={selectedLocation}
        />
      )}

      <Footer />
    </div>
  );
}
